import React, { useEffect, useMemo, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import { useNavigate } from 'react-router-dom';
import axios from '../api';
import { useAuth } from '../contexts/AuthContext';
import customerService from '../services/customer.service';
import merchantService from '../services/merchant.service';
import deliveryService from '../services/delivery.service';
import PaginationControls from '../components/PaginationControls';
import LoadingAnimation from '../components/LoadingAnimation';
import PageHeader from '../components/ui/PageHeader';
import {
  Box, Card, Chip, Dialog, DialogContent, DialogTitle, FormControl, IconButton,
  InputAdornment, InputLabel, Menu, MenuItem, Select, SelectChangeEvent, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
  Alert, Snackbar, Button, Divider, Paper, Rating,
} from '@mui/material';
import {
  Search, Close, Visibility as VisibilityIcon, Download,
  Receipt as InvoiceIcon, Autorenew as StatusIcon,
  CheckCircle as AcceptIcon, Cancel as RejectIcon, Payments as PaidIcon,
  Star as StarIcon,
} from '@mui/icons-material';

type OrderStatus = 'Reserved' | 'Payment successful' | 'Ready for pickup' | 'Picked up' | 'Cancelled';
type PaymentStatus = 'Paid' | 'Pending' | 'Refunded' | 'Failed';

interface Order {
  id: string;
  _id?: string;
  customer: string;
  merchant: string;
  box: string;
  amount: number;
  status: OrderStatus;
  payment: PaymentStatus;
  date: string;
  customerId?: string;
  businessId?: string;
  boxId?: string;
  quantity?: number;
  createdAt?: string;
  // Raw backend payment fields, kept for action gating (mark-as-paid)
  paymentMethod?: string;
  paymentStatusRaw?: string;
  // Regular menu items added alongside the surprise box, if any
  menuItems?: { name: string; quantity: number; price: number }[];
}

const normalizeOrder = (item: any): Order => {
  const firstItem = item.items?.[0];
  const businessObj = firstItem?.business || item.business;
  const surpriseBoxObj = firstItem?.surpriseBox || item.surpriseBox;
  const menuItems = (item.menuItems || []).map((mi: any) => ({
    name: (typeof mi.menuItem === 'object' && mi.menuItem?.name) || 'Menu item',
    quantity: mi.quantity || 1,
    price: mi.price || 0,
  }));
  // A menu-items-only order (no surprise box at all) has no `items` line to
  // read the merchant/title from — fall back to the first menu line's own
  // populated business, same fallback Order.fromJson uses on the Flutter side.
  const firstMenuBusiness = item.menuItems?.[0]?.business;
  const isMenuOnly = !firstItem && item.menuItems?.length > 0;
  const merchantName =
    (typeof businessObj === 'object' && businessObj?.name) ||
    (typeof surpriseBoxObj === 'object' && surpriseBoxObj?.business?.name) ||
    (isMenuOnly && typeof firstMenuBusiness === 'object' && firstMenuBusiness?.name) ||
    item.merchant ||
    'Unknown merchant';
  const boxName =
    (typeof surpriseBoxObj === 'object' && surpriseBoxObj?.name) ||
    (isMenuOnly
      ? menuItems.length === 1
        ? menuItems[0].name
        : `${menuItems[0]?.name || 'Menu item'} +${menuItems.length - 1} more`
      : null) ||
    item.box ||
    'Unknown box';
  const quantity = item.items?.reduce((sum: number, i: any) => sum + (i.quantity || 0), 0) || item.quantity || 1;

  return {
    id: item.orderNumber || String(item._id || item.id),
    _id: String(item._id || item.id),
    customer: item.customer?.name || item.customer || 'Unknown customer',
    merchant: merchantName,
    box: boxName,
    amount: item.pricing?.total ?? item.amount ?? 0,
    status: item.status === 'pending' ? 'Reserved' : item.status === 'confirmed' ? 'Payment successful' : item.status === 'ready' ? 'Ready for pickup' : item.status === 'collected' ? 'Picked up' : item.status === 'cancelled' ? 'Cancelled' : 'Reserved',
    payment: item.payment?.status === 'completed' ? 'Paid' : item.payment?.status === 'refunded' ? 'Refunded' : item.payment?.status === 'failed' ? 'Failed' : 'Pending',
    date: item.createdAt ? new Date(item.createdAt).toLocaleString() : item.date ?? 'N/A',
    customerId: item.customer?._id || item.customerId,
    businessId: (typeof businessObj === 'object' && (businessObj?._id || businessObj?.id)) || item.businessId,
    boxId: (typeof surpriseBoxObj === 'object' && (surpriseBoxObj?._id || surpriseBoxObj?.id)) || item.boxId,
    quantity,
    createdAt: item.createdAt,
    paymentMethod: item.payment?.method || item.paymentMethod,
    paymentStatusRaw: item.payment?.status || 'pending',
    menuItems,
  };
};

const statusColor: Record<OrderStatus, 'success' | 'info' | 'warning' | 'default' | 'error'> = {
  Reserved: 'info', 'Payment successful': 'info', 'Ready for pickup': 'warning', 'Picked up': 'success', Cancelled: 'error',
};

// Backend order-status values (validated by PATCH /api/orders/:id) mapped to the
// friendly labels shown in the UI. handleStatusChange sends the backend value.
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'pending', label: 'Reserved' },
  { value: 'confirmed', label: 'Payment successful' },
  { value: 'ready', label: 'Ready for pickup' },
  { value: 'collected', label: 'Picked up' },
  { value: 'cancelled', label: 'Cancelled' },
];

// A delivery record wraps an order but tracks its own lifecycle. Map each
// delivery status onto the closest backend order-status token so normalizeOrder
// renders a sensible label in the shared Orders table for the "My Deliveries" view.
const DELIVERY_TO_ORDER_STATUS: Record<string, string> = {
  pending: 'confirmed',
  assigned: 'confirmed',
  accepted: 'confirmed',
  in_transit: 'ready',
  out_for_delivery: 'ready',
  picked_up: 'ready',
  delivered: 'collected',
  completed: 'collected',
  cancelled: 'cancelled',
  rejected: 'cancelled',
};

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCustomer = user?.role === 'customer';
  const isMerchant = user?.role === 'merchant';
  const isDelivery = user?.role === 'delivery';
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [paymentFilter, setPaymentFilter] = useState('All');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [serverStats, setServerStats] = useState<{ paid: number; collected: number; revenue: number } | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuOrder, setStatusMenuOrder] = useState<Order | null>(null);
  const [reviewDialog, setReviewDialog] = useState<{ open: boolean; order: Order | null }>({ open: false, order: null });
  const [reviewRatings, setReviewRatings] = useState({ box: 0, restaurant: 0 });
  const [reviewComment, setReviewComment] = useState('');

  useEffect(() => {
    fetchOrders();
  }, [page, rowsPerPage]);

  const fetchOrders = async () => {
    try {
      let ordersData;

      if (isCustomer) {
        // Use customer service for customer orders
        ordersData = await customerService.getOrders(page, rowsPerPage);
      } else if (isMerchant) {
        // Use merchant service for merchant orders (only their business)
        ordersData = await merchantService.getOrders({ page, limit: rowsPerPage });
      } else if (isDelivery) {
        // Delivery drivers see their own deliveries. Each delivery wraps a
        // (deep-populated) order; represent it by that order, tagged with the
        // delivery's own status so the table shows where the job stands. Uses
        // /api/delivery/my-deliveries, not the admin-only /api/admin/orders.
        const res = await deliveryService.getMyDeliveries({ page, limit: rowsPerPage });
        const list = res.data || [];
        ordersData = {
          orders: list.map((d: any) => ({
            ...(d.order || {}),
            _id: (d.order && d.order._id) || d._id,
            status: DELIVERY_TO_ORDER_STATUS[d.status] || d.order?.status || 'confirmed',
          })),
          total: res.pagination?.total,
        };
      } else {
        // Use admin endpoint for admin (all orders)
        const response = await axios.get('/api/admin/orders', { params: { page, limit: rowsPerPage } });
        ordersData = response.data;
      }

      const ordersArray = ordersData.orders || ordersData;
      setOrders(Array.isArray(ordersArray) ? ordersArray.map(normalizeOrder) : []);
      setTotal(ordersData.total ?? ordersArray.length);
      setServerStats(ordersData.stats || null);
      setError(false);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const matchesQuery = `${order.id} ${order.customer} ${order.merchant} ${order.box}`.toLowerCase().includes(query.toLowerCase());
      const matchesStatus = statusFilter === 'All' || order.status === statusFilter;
      const matchesPayment = paymentFilter === 'All' || order.payment === paymentFilter;
      return matchesQuery && matchesStatus && matchesPayment;
    });
  }, [orders, query, statusFilter, paymentFilter]);

  // "total" uses the already-correct pagination total (every role's
  // endpoint returns it) rather than orders.length, which was only ever
  // the current page's size. paid/collected/revenue prefer the backend's
  // platform-wide aggregate (admin + merchant endpoints now return one) so
  // they don't undercount once there's more than one page of orders.
  const stats = {
    total,
    paid: serverStats?.paid ?? orders.filter(o => o.payment === 'Paid').length,
    collected: serverStats?.collected ?? orders.filter(o => o.status === 'Picked up').length,
    revenue: serverStats?.revenue ?? orders.filter(o => o.payment === 'Paid').reduce((sum, o) => sum + o.amount, 0),
  };

  const handleViewDetails = (order: Order) => {
    if (isCustomer || isMerchant) {
      // Navigate to dedicated order details page for customers/merchants using MongoDB _id
      navigate(`/orders/${order._id || order.id}`);
    } else {
      // Show dialog for admin
      setSelectedOrder(order);
      setDetailsOpen(true);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      if (isMerchant) {
        await merchantService.updateOrderStatus(orderId, newStatus.toLowerCase());
      } else {
        await axios.patch(`/api/orders/${orderId}`, { status: newStatus.toLowerCase() });
      }
      fetchOrders();
      // Prompt for review ~30% of the time when order is collected, only for customers
      if (newStatus === 'collected' && isCustomer && Math.random() < 0.3) {
        const order = orders.find(o => (o._id || o.id) === orderId) || null;
        setReviewRatings({ box: 0, restaurant: 0 });
        setReviewComment('');
        setReviewDialog({ open: true, order });
      }
      setSnackbar({ open: true, message: 'Order status updated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update order', severity: 'error' });
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewDialog.order) return;
    try {
      await axios.post(`/api/orders/${reviewDialog.order._id || reviewDialog.order.id}/review`, {
        rating: reviewRatings.restaurant,
        boxRating: reviewRatings.box,
        comment: reviewComment,
      });
      setSnackbar({ open: true, message: 'Thanks for your review! 🙏', severity: 'success' });
    } catch (err) {
      // Silently ignore — review is optional
    } finally {
      setReviewDialog({ open: false, order: null });
    }
  };

  const handleMarkPaid = async (order: Order) => {
    const orderId = order._id || order.id;
    try {
      if (isMerchant) {
        await merchantService.markOrderPaid(orderId);
      } else {
        // Admin uses the generic order API
        await axios.patch(`/api/orders/${orderId}`, { paymentStatus: 'completed' });
      }
      fetchOrders();
      setSnackbar({ open: true, message: 'Payment marked as received', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update payment', severity: 'error' });
    }
  };

  const openStatusMenu = (event: React.MouseEvent<HTMLElement>, order: Order) => {
    setStatusMenuAnchor(event.currentTarget);
    setStatusMenuOrder(order);
  };

  const closeStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusMenuOrder(null);
  };

  const handleSelectStatus = async (value: string) => {
    const order = statusMenuOrder;
    closeStatusMenu();
    if (order) await handleStatusChange(order._id || order.id, value);
  };

  const handleExportData = () => {
    const csv = [
      ['Order ID', 'Customer', 'Merchant', 'Box', 'Amount (OMR)', 'Status', 'Payment', 'Date'],
      ...filteredOrders.map(o => [o.id, o.customer, o.merchant, o.box, o.amount.toFixed(2), o.status, o.payment, o.date]),
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    setSnackbar({ open: true, message: 'Orders exported successfully', severity: 'success' });
  };

  const handleDownloadInvoice = async (order: Order) => {
    try {
      // Fetch full order details
      const orderDetails = await customerService.getOrderDetails(order._id || order.id);

      // Generate HTML invoice
      const invoiceHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice - ${order.id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Arial', sans-serif; padding: 40px; background: #f5f5f5; }
    .invoice { max-width: 800px; margin: 0 auto; background: white; padding: 40px; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
    .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px; border-bottom: 3px solid #0B6E4F; padding-bottom: 20px; }
    .logo { font-size: 32px; font-weight: bold; color: #0B6E4F; }
    .logo .bite { color: #FF7A00; }
    .logo .hub { color: #0B6E4F; }
    .company-info { text-align: right; color: #666; font-size: 14px; }
    .invoice-title { font-size: 28px; color: #333; margin-bottom: 30px; }
    .invoice-details { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .detail-group { flex: 1; }
    .detail-label { font-weight: bold; color: #666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px; }
    .detail-value { color: #333; font-size: 14px; margin-bottom: 15px; }
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
    .items-table th { background: #0B6E4F; color: white; padding: 12px; text-align: left; font-size: 12px; text-transform: uppercase; }
    .items-table td { padding: 12px; border-bottom: 1px solid #eee; }
    .items-table tr:last-child td { border-bottom: none; }
    .totals { margin-left: auto; width: 300px; }
    .total-row { display: flex; justify-content: space-between; padding: 8px 0; }
    .total-row.grand { font-size: 20px; font-weight: bold; color: #0B6E4F; border-top: 2px solid #0B6E4F; margin-top: 10px; padding-top: 15px; }
    .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #eee; text-align: center; color: #666; font-size: 12px; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: bold; text-transform: uppercase; }
    .status-paid { background: #d4edda; color: #155724; }
    .status-pending { background: #fff3cd; color: #856404; }
    .qr-section { text-align: center; margin: 30px 0; padding: 20px; background: #f8f9fa; border-radius: 8px; }
    @media print {
      body { background: white; padding: 0; }
      .invoice { box-shadow: none; padding: 20px; }
      @page {
        margin: 0;
        size: A4;
      }
      @page :first {
        margin-top: 0;
      }
    }
  </style>
</head>
<body>
  <div class="invoice">
    <div class="header">
      <div class="logo">
        <span class="bite">bite</span><span class="hub">hub</span>
      </div>
      <div class="company-info">
        <div style="font-weight: bold; font-size: 16px; color: #0B6E4F; margin-bottom: 5px;">BiteHub</div>
        <div>Keep Good Food in the Loop</div>
        <div>Fight Food Waste Together</div>
      </div>
    </div>

    <div class="invoice-title">INVOICE</div>

    <div class="invoice-details">
      <div class="detail-group">
        <div class="detail-label">Invoice Number</div>
        <div class="detail-value">${order.id}</div>

        <div class="detail-label">Date</div>
        <div class="detail-value">${order.date}</div>

        <div class="detail-label">Payment Status</div>
        <div class="detail-value">
          <span class="status-badge ${order.payment === 'Paid' ? 'status-paid' : 'status-pending'}">
            ${order.payment}
          </span>
        </div>
      </div>

      <div class="detail-group">
        <div class="detail-label">Customer</div>
        <div class="detail-value">${order.customer}</div>

        <div class="detail-label">Order Status</div>
        <div class="detail-value">${order.status}</div>
      </div>
    </div>

    <table class="items-table">
      <thead>
        <tr>
          <th>Description</th>
          <th>Merchant</th>
          <th style="text-align: center;">Quantity</th>
          <th style="text-align: right;">Price</th>
          <th style="text-align: right;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${orderDetails.items?.map((item: any) => `
          <tr>
            <td><strong>${item.surpriseBox?.name || 'Surprise Box'}</strong></td>
            <td>${item.surpriseBox?.business?.name || item.business?.name || order.merchant}</td>
            <td style="text-align: center;">${item.quantity}</td>
            <td style="text-align: right;">OMR ${item.price.toFixed(2)}</td>
            <td style="text-align: right;">OMR ${(item.quantity * item.price).toFixed(2)}</td>
          </tr>
        `).join('') || `
          <tr>
            <td><strong>${order.box}</strong></td>
            <td>${order.merchant}</td>
            <td style="text-align: center;">${order.quantity || 1}</td>
            <td style="text-align: right;">OMR ${order.amount.toFixed(2)}</td>
            <td style="text-align: right;">OMR ${order.amount.toFixed(2)}</td>
          </tr>
        `}
        ${(orderDetails.menuItems || []).map((mi: any) => `
          <tr>
            <td><strong>${mi.menuItem?.name || 'Menu item'}</strong></td>
            <td>${mi.menuItem?.business?.name || mi.business?.name || order.merchant}</td>
            <td style="text-align: center;">${mi.quantity}</td>
            <td style="text-align: right;">OMR ${(mi.price || 0).toFixed(2)}</td>
            <td style="text-align: right;">OMR ${((mi.quantity || 1) * (mi.price || 0)).toFixed(2)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="totals">
      <div class="total-row">
        <span>Subtotal:</span>
        <span>OMR ${orderDetails.pricing?.subtotal?.toFixed(2) || order.amount.toFixed(2)}</span>
      </div>
      ${orderDetails.pricing?.discount ? `
      <div class="total-row" style="color: #28a745;">
        <span>Discount:</span>
        <span>- OMR ${orderDetails.pricing.discount.toFixed(2)}</span>
      </div>
      ` : ''}
      <div class="total-row">
        <span>Delivery Fee:</span>
        <span style="color: #28a745;">FREE</span>
      </div>
      <div class="total-row grand">
        <span>TOTAL:</span>
        <span>OMR ${orderDetails.pricing?.total?.toFixed(2) || order.amount.toFixed(2)}</span>
      </div>
    </div>

    <div class="footer">
      <div style="margin-bottom: 10px; font-weight: bold; color: #0B6E4F;">Thank you for helping reduce food waste! 🌍</div>
      <div>This is a computer-generated invoice and does not require a signature.</div>
      <div style="margin-top: 10px;">Questions? Contact us at support@bitehub.com</div>
    </div>
  </div>
</body>
</html>
      `;

      // Open in new window and print
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(invoiceHTML);
        printWindow.document.close();

        // Wait for images and content to load
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();

          // Add event listener to close window after print
          printWindow.onafterprint = function() {
            printWindow.close();
          };
        }, 250);
      }

      setSnackbar({ open: true, message: 'Opening print dialog...', severity: 'success' });
    } catch (error) {
      console.error('Error generating invoice:', error);
      setSnackbar({ open: true, message: 'Failed to generate invoice', severity: 'error' });
    }
  };

  return (
    <Box>
      <PageHeader
        title={isCustomer ? 'My Orders' : isDelivery ? 'My Deliveries' : 'Orders'}
        subtitle={
          isCustomer
            ? 'View your order history and download invoices'
            : isDelivery
            ? `${stats.total.toLocaleString()} ${stats.total === 1 ? 'delivery' : 'deliveries'} assigned to you`
            : `${stats.total.toLocaleString()} shown \u00B7 ${stats.paid.toLocaleString()} paid \u00B7 ${stats.collected.toLocaleString()} collected \u00B7 OMR ${stats.revenue.toFixed(2)} revenue`
        }
        actions={
          !isCustomer && !isDelivery && (
            <Button variant="contained" startIcon={<Download />} onClick={handleExportData} disabled={filteredOrders.length === 0}>
              Export
            </Button>
          )
        }
      />

      {/* Stats Cards */}
      {!isCustomer && !isDelivery && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
          {[
            { label: 'Total Orders', value: stats.total.toLocaleString(), color: 'text.primary' },
            { label: 'Paid Orders', value: stats.paid.toLocaleString(), color: 'success.main' },
            { label: 'Collected', value: stats.collected.toLocaleString(), color: 'text.primary' },
            { label: 'Total Revenue', value: <><RialSymbol /> {stats.revenue.toFixed(2)}</>, color: 'text.primary' },
          ].map((s) => (
            <Paper key={s.label} sx={{ p: 2.5, borderRadius: 3.5 }}>
              <Typography sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 500, mb: 0.75 }}>{s.label}</Typography>
              <Typography className="tabular-nums" sx={{ fontWeight: 700, fontSize: 24, letterSpacing: '-0.02em', color: s.color }}>
                {s.value}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      <Card>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search orders"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Order Status</InputLabel>
            <Select value={statusFilter} label="Order Status" onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}>
              {['All', 'Reserved', 'Payment successful', 'Ready for pickup', 'Picked up', 'Cancelled'].map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Payment</InputLabel>
            <Select value={paymentFilter} label="Payment" onChange={(e: SelectChangeEvent) => setPaymentFilter(e.target.value)}>
              {['All', 'Paid', 'Pending', 'Refunded', 'Failed'].map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ m: 2 }}>Unable to load orders. Check that the backend is running.</Alert>}

        {!error && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Order ID</strong></TableCell>
                  {!isCustomer && <TableCell><strong>Customer</strong></TableCell>}
                  <TableCell><strong>Merchant / Box</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Payment</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <LoadingAnimation message="Loading orders..." subtitle="Fetching order data" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No orders match these filters.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell><Typography sx={{ fontWeight: 600 }}>{order.id}</Typography></TableCell>
                    {!isCustomer && <TableCell>{order.customer}</TableCell>}
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{order.merchant}</Typography>
                      <Typography variant="caption" color="text.secondary">{order.box}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}><RialSymbol /> {order.amount.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip label={order.status} color={statusColor[order.status]} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.payment}
                        color={order.payment === 'Paid' ? 'success' : order.payment === 'Failed' ? 'error' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{order.date}</TableCell>
                    <TableCell align="center">
                      {isCustomer ? (
                        <>
                          <IconButton size="small" color="primary" onClick={() => handleViewDetails(order)} title="View details">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="success" onClick={() => handleDownloadInvoice(order)} title="Download invoice">
                            <InvoiceIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : isDelivery ? (
                        <IconButton size="small" color="primary" onClick={() => handleViewDetails(order)} title="View details">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <>
                          <IconButton size="small" color="primary" onClick={() => handleViewDetails(order)} title="View details">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          {order.status === 'Reserved' && (
                            <>
                              <IconButton size="small" color="success" onClick={() => handleStatusChange(order._id || order.id, 'confirmed')} title="Accept order">
                                <AcceptIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleStatusChange(order._id || order.id, 'cancelled')} title="Reject order">
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                          {order.paymentMethod === 'cash' && order.paymentStatusRaw !== 'completed' && order.status !== 'Cancelled' && (
                            <IconButton size="small" color="primary" onClick={() => handleMarkPaid(order)} title="Mark cash as paid">
                              <PaidIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton size="small" onClick={(e) => openStatusMenu(e, order)} title="Change status">
                            <StatusIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <PaginationControls
        page={page}
        total={total}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }}
      />

      {/* Order Details Dialog */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        <DialogTitle>
          Order Details
          <IconButton onClick={handleCloseDetails} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Order ID</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedOrder.id}</Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
                <Typography>{selectedOrder.customer}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Merchant</Typography>
                <Typography>{selectedOrder.merchant}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Box</Typography>
                <Typography>{selectedOrder.box}</Typography>
              </Box>

              {selectedOrder.menuItems && selectedOrder.menuItems.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Extra Menu Items
                  </Typography>
                  <Stack spacing={0.5}>
                    {selectedOrder.menuItems.map((mi, index) => (
                      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{mi.quantity}x {mi.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          <RialSymbol /> {(mi.quantity * mi.price).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: 'primary.main' }}><RialSymbol /> {selectedOrder.amount.toFixed(2)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Order Status</Typography>
                  <Chip label={selectedOrder.status} color={statusColor[selectedOrder.status]} size="small" sx={{ mt: 1 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Payment</Typography>
                  <Chip label={selectedOrder.payment} color={selectedOrder.payment === 'Paid' ? 'success' : 'error'} size="small" sx={{ mt: 1 }} variant="outlined" />
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                <Typography>{selectedOrder.date}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Status change menu (admin / merchant) */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={closeStatusMenu}>
        {STATUS_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={opt.label === statusMenuOrder?.status}
            onClick={() => handleSelectStatus(opt.value)}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onClose={() => setReviewDialog({ open: false, order: null })} maxWidth="xs" fullWidth>
        <DialogTitle>
          How was your order? ⭐
          <IconButton onClick={() => setReviewDialog({ open: false, order: null })} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              You picked up <strong>{reviewDialog.order?.box}</strong> from <strong>{reviewDialog.order?.merchant}</strong>. Share your experience!
            </Typography>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Food Box Rating</Typography>
              <Rating value={reviewRatings.box} onChange={(_, v) => setReviewRatings(r => ({ ...r, box: v || 0 }))} size="large" />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Restaurant Rating</Typography>
              <Rating value={reviewRatings.restaurant} onChange={(_, v) => setReviewRatings(r => ({ ...r, restaurant: v || 0 }))} size="large" />
            </Box>
            <TextField
              multiline
              rows={2}
              label="Comment (optional)"
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => setReviewDialog({ open: false, order: null })} color="inherit">Skip</Button>
              <Button
                variant="contained"
                onClick={handleSubmitReview}
                disabled={reviewRatings.box === 0 && reviewRatings.restaurant === 0}
                startIcon={<StarIcon />}
              >
                Submit Review
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
